import { z } from "zod";

declare const OPERATOR_WRAPPED: unique symbol;

/**
 * The operator-nested branch of a filter value, as seen by the TYPE system.
 * Nominal on purpose: a structural `Record<string, T>` would also match a
 * `dateRange`'s `{ start, end, label }` value (an anonymous object type gets an
 * implicit index signature), so `useFilters` could not tell "unwrap this" from
 * "this IS the value" and would flatten date values to `string`.
 *
 * Nothing ever holds this brand at runtime — `parseFilterSearch` strips the
 * wrapper before any consumer reads it. It exists only so `UnwrapOperators` can
 * `Exclude` exactly this union member and nothing else.
 */
export type OperatorWrapped<T> = Record<string, T> & {
  readonly [OPERATOR_WRAPPED]: true;
};

/** Drop the operator-nested branch, leaving the value type a route field actually reads. */
export type UnwrapOperators<V> = Exclude<V, OperatorWrapped<any>>;

/**
 * Wrap a route's filter-value schema so it also accepts the operator-nested URL
 * form (`filters[status][notIn]=A` alongside the bare `filters[status]=A`).
 * Both branches carry the SAME value schema — only the nesting differs — and
 * `parseFilterSearch` flattens them before any consumer sees them.
 *
 *   filters: z.object({
 *     status: withOperators(z.string().optional()),
 *     assigneeIds: withOperators(commaListSchema),
 *   }).optional(),
 *
 * The record branch is deliberately `z.record(z.string(), …)` rather than an
 * enum of operators: a strict enum throws `SearchParamError` and takes the page
 * down on a stale link, whereas an unknown operator is narrowed back to the
 * field's default by `resolveOperator`.
 *
 * NOT for `dateRange` / `priceRange`. Their values are already objects, their
 * operator is derived from which bounds are filled (see `DERIVED_OPERATOR_TYPES`),
 * and a `z.object` strips unknown keys — so an operator-nested value would parse
 * as an empty object instead of failing over to the record branch. Those two
 * types keep their plain value schema (`filterDateRangeSchema`, the price
 * `{ min, max }` object).
 */
export function withOperators<T extends z.ZodTypeAny>(value: T) {
  // The cast brands the record branch for `UnwrapOperators`; the schema that
  // actually validates at runtime is the unbranded record built here.
  const nested = z.record(z.string(), value) as unknown as z.ZodType<
    OperatorWrapped<z.output<T>>
  >;
  return z.union([value, nested]).optional();
}
